import { apiFetch, ApiError, clearStoredAuthHeaders, SSO_API_URL } from './client';

const SSO_CLIENT_ID = process.env.REACT_APP_SSO_CLIENT_ID ?? '';

interface User {
  uuid: string;
  email: string;
}

const login = async (email: string, password: string): Promise<User> => {
  const body = await apiFetch('/auth/sign_in', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  return body.data;
};

const logout = async (): Promise<void> => {
  try {
    await apiFetch('/auth/sign_out', { method: 'DELETE' });
  } finally {
    // Log the user out locally even if the server call fails (e.g. the
    // token already expired) — there's nothing useful to retry here.
    clearStoredAuthHeaders();
  }
};

const validateToken = async (): Promise<User | null> => {
  try {
    const body = await apiFetch('/auth/validate_token', { method: 'GET' });
    return body.data;
  } catch {
    return null;
  }
};

// Registration goes through /api/v1/users, not DeviseTokenAuth's /auth,
// and is authorized as this app's registered public ClientApp (client_id
// only, no secret — public/browser clients can't hold one safely) rather
// than a signed-in user's token.
const register = async (email: string, password: string, passwordConfirm: string): Promise<User> => {
  const response = await fetch(`${SSO_API_URL}/api/v1/users`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Basic ${btoa(`${SSO_CLIENT_ID}:`)}`,
    },
    body: JSON.stringify({ email, password, passwordConfirm }),
  });

  const text = await response.text();
  const body = text ? JSON.parse(text) : null;

  if (!response.ok) {
    throw new ApiError(response.status, body);
  }

  return body;
};

const requestPasswordReset = async (email: string): Promise<void> => {
  await apiFetch('/auth/password', {
    method: 'POST',
    body: JSON.stringify({
      email,
      redirect_url: `${window.location.origin}/reset-password`,
    }),
  });
};

const submitNewPassword = async (
  resetPasswordToken: string,
  password: string,
  passwordConfirmation: string
): Promise<void> => {
  await apiFetch('/auth/password', {
    method: 'PUT',
    body: JSON.stringify({
      reset_password_token: resetPasswordToken,
      password,
      password_confirmation: passwordConfirmation,
    }),
  });
};

export type {
  User,
};

export {
  login,
  logout,
  validateToken,
  register,
  requestPasswordReset,
  submitNewPassword,
};
